import { ReactElement } from "react";
import HeroSection from "@components/severalbrands/HeroSection";
import MetaTags from "@components/severalbrands/metaTags";
// Main general category shared components
import GeneralCategorySecondSection from "../b2b/secondSection";
import GeneralCategoryThirdSection from "../b2b/thirdSection";
import GeneralCategoryForthSection from "../b2b/forthSection";
// images and icons
import B2BLeadCultivationIcon from "../b2b/secondSection/strategicLeadGeneration/icons/b2bLeadCultivationIcon";
import CostPerCallIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerCallIcon";
import CostPerClickIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerClickIcon";
import CostPerLeadIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerLeadIcon";
import CostPerRetainerIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerRetainerIcon";
import CostPerSaleIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerSaleIcon";
import RevenueShareIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/revenueShareIcon";
import { partnerWitUSData } from "@components/severalbrands/homePage/forthSection";
import { useDomainContext } from "@hooks/useDomainContext";
// category data
const imgTextComponentContent = {
    img: "/severalbrands/assets/images/businessPhoneSolution.webp",
    mainTitle: "Enhance Your Business Phone System Sales",
    paragraphs: [
        "The business phone systems market is projected to grow significantly, reaching $82 billion by 2029, driven by the rising demand for unified communications, VoIP technology, and cloud-based solutions. Businesses today are seeking phone systems that offer seamless integration, advanced features, and robust security.",
        "To stand out in the competitive business phone systems market, having more than just top-notch products is essential. Our expert digital marketing services can optimize your sales funnel and generate high-quality leads that turn into loyal customers. Our tailored marketing strategies are designed to expand your reach and impact, aligning with your business objectives. Fill out the form above to schedule a call with us and explore how we can help transform your business potential into lasting success.",
    ],
    showAnimation: true,
    showButton: true,
    buttonTitle: "Schedule a Call",
};
const strategicLeadGenerationContent = {
    title: "Strategic Lead Generation: Timing Meets Precision",
    topDescription:
        "Our prowess in spearheading lead-generation efforts across diverse industries stems from our unique approach.",
    bottomDescription:
        "Outsourcing your lead generation isn't just a time saver—it's a cost-effective strategy to boost your business's growth. Partner with us for unparalleled lead generation expertise, allowing you to concentrate on perfecting your offerings and achieving outstanding results for your clients.",
    list: [
        {
            title: "B2B Lead Cultivation",
            tabName: "B2B",
            slug: "b2b",
            description:
                "A shocking finding by Marketing Donut reveals that 80% of leads go unexplored, resulting in a wide expanse of lost opportunities. Our advanced methods and systems are designed to identify and cultivate genuine leads, ensuring every potential client is engaged through follow-up messages or direct calls. This approach positions us to deliver high-quality prospects to your business, enhancing customer loyalty. We have vast experience working with a wide range of industries and helping your company to serve other businesses.",
            icon: <B2BLeadCultivationIcon />,
        },
    ],
};
const howWeHelpContent = {
    mainTitle: "How We Help Your Business Attract More Customers",
    list: [
        {
            title: "Cost Per Click",
            p: "Our comparison and review websites can drive highly qualified traffic to your offer.",
            icon: <CostPerClickIcon />,
        },
        {
            title: "Cost Per Sale",
            p: "Our revenue model is sales-oriented, ensuring we are compensated solely upon delivering tangible sales outcomes for your business.",
            icon: <CostPerSaleIcon />,
        },
        {
            title: "Cost Per Lead",
            p: "We excel at generating top-notch web form leads using targeted paid search, social media, display ads, organic SEO, and other advertising campaigns.",
            icon: <CostPerLeadIcon />,
        },
        {
            title: "Cost Per Call",
            p: "We assist local businesses in boosting their customer calls through our billable call service. We only charge for calls that either meet a set duration threshold or result in a conversion.",
            icon: <CostPerCallIcon />,
        },
        {
            title: "Performance-Based Cost Structure",
            p: "Law firms can partner with us risk-free. Payment is contingent on signing the retainer, handled by either your intake team or our reliable intake center.",
            icon: <CostPerRetainerIcon />,
        },
        {
            title: "Revenue Share",
            p: "Under this model, we produce top-tier leads for your business, and you compensate us solely based on a percentage of the revenue derived from those leads.",
            icon: <RevenueShareIcon />,
        },
    ],
};
const howWeWorkContent = {
    title: "How We Work",
    list: [
        {
            description:
                "Our company works with your business to create a pricing strategy that aligns with your performance metrics and meets your requirements.",
        },
        {
            description:
                "Our team conducts thorough research to generate leads tailored to your specific needs and ideal customer profile (ICP). We then create personalized campaigns targeting actual customers through comprehensive research to maximize conversions.",
        },
        {
            description:
                "Our advanced lead verification system filters out fraud, checks syntax, and flags duplicates. Verified leads are nurtured by our call center team, using keyword optimization and strategic timing to boost conversions. Each lead is verified before being promptly sent to you.",
        },
        {
            description:
                "We request regular reports from our clients on lead performance, including contact status and sales conversion rates. This helps us to improve lead quality and find the perfect customers for our clients.",
        },
        {
            description:
                "We agree on KPIs with our partners for a reasonable cost per sale or opportunity and adjust payouts based on performance against these KPIs.",
        },
        {
            description:
                "Continue following steps 2, 3, and 4 until we achieve an autopilot mode that meets our business's KPIs and satisfies everyone involved.",
        },
    ],
};

const unlockBusinessContent = [
    {
        img: "/severalbrands/assets/images/unlockBusinessSuccessB2b2.webp",
        mainTitle:
            "Unlock Business Success With Diverse Marketing Channels & Tactics",
        secondTitle: "SEO & SEM",
        paragraphs: [
            "While SEO offers lasting benefits and revenue growth without added expenses, it may lack scalability. To ramp up sales for our clients, we employ SEM. Our approach involves a dedicated content creation team for SEO and more than 20 SEM account managers overseeing paid media campaigns across Google, Bing, and Yahoo, guaranteeing effective and efficient digital marketing solutions.",
        ],

        mainSectionClassName: `3xl:gap-[200px] lg:gap-[130px] gap-[40px] `,
        mainTitleClassName: "text-[#351C4F]",
        secondTitleClassName: "mt-[20px] text-[#37367D]",
        imgClassName:
            "w-[250px]  h-[250px] md:w-[420px] md:min-w-[420px] md:h-[420px] relative 3xl:w-[620px] 3xl:min-w-[620px] 3xl:h-[620px] lg:mt-10 lg:ml-16 3xl:ml-28",
    },

    {
        img: "/severalbrands/assets/images/displayAdvertisingB2b.png",
        mainTitle: "Display Advertising",
        paragraphs: [
            "We implement diverse sources in our display advertising strategies, leveraging platforms like the Google Display Network, native advertising channels such as Taboola and Outbrain, PPV advertising, and other third-party networks. With over a decade of experience, we've rigorously tested and refined our methods across these networks to deliver top-notch quality and maximize conversion metrics for our advertising campaigns.",
        ],
        mainSectionClassName: `lg:gap-[160px] gap-[40px] lg:ml-16 3xl:ml-28 `,

        mainTitleClassName: "text-[#37367D] 3xl:mt-[60px]",
        imgToLeft: true,
        imgClassName:
            "w-[250px]  h-[200px] md:w-[458px] md:min-w-[458px] md:h-[312px] relative 3xl:w-[630px] 3xl:min-w-[630px] 3xl:h-[554px]",
    },

    {
        img: "/severalbrands/assets/images/localizationB2b2.webp",
        mainTitle: "Localization",
        paragraphs: [
            "Localization is a potent marketing tool, particularly for clients with global outreach or diverse language audiences. US-based law firms aiming at English- and Spanish-speaking clientele can gain substantial advantages. Likewise, software companies offering antivirus, website builders, hosting services, etc., can leverage localization to enhance both paid media and organic traffic.",
        ],
        mainSectionClassName: `3xl:gap-[200px] lg:gap-[180px]  gap-[40px]  `,
        secondSectionClassName: "",
        mainTitleClassName: "text-[#37367D] lg:mt-10",

        imgClassName:
            "w-[250px]  h-[250px] md:w-[392px] md:min-w-[392px] md:h-[392px] 3xl:w-[592px] 3xl:min-w-[592px] 3xl:h-[592px]  relative lg:ml-16 3xl:ml-28",
    },
];
export default function BusinessPhoneSystemsContainer(): ReactElement {
    const { category } = useDomainContext();
    const description = category?.metatags?.find(
        (meta) => meta.key === "description",
    )?.value;
    return (
        <>
            <MetaTags
                title={
                    category?.title ??
                    "Boost Your Business Phone System Leads with Zero Risk | SeveralBrands.com"
                }
                description={
                    description ??
                    "Partner with us to enhance your lead generation for business phone systems. Discover how we can boost your sales growth. Book your call today!"
                }
            />
            <HeroSection />
            <GeneralCategorySecondSection
                mainData={{
                    imgTextComponentContent: imgTextComponentContent,
                    strategicLeadGenerationContent:
                        strategicLeadGenerationContent,
                }}
            />
            <GeneralCategoryThirdSection
                mainData={{
                    howWeHelpContent: howWeHelpContent,
                    howWeWorkContent: howWeWorkContent,
                }}
            />
            <GeneralCategoryForthSection
                mainData={{
                    partnerWithUSData: partnerWitUSData,
                    unlockBusinessContent: unlockBusinessContent,
                }}
            />
        </>
    );
}
